import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kExternalStorageBasePath } from '@/constants/constants-api-paths'
import { kErrorCodeAuthError } from '@/constants/constants-error-codes'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { MatterExternalSyncMappingDisconnectPreview, MatterExternalSyncMappingDisconnectPreviewSchema } from '@/user_files/external-storage/schemas'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'

export async function getMatterExternalSyncMappingDisconnectPreview(matterId: string): Promise<MatterExternalSyncMappingDisconnectPreview> {
  const sentryTransactionId = nanoid()

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw Error(kErrorCodeAuthError)

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kExternalStorageBasePath}/mappings/matter/${encodeURIComponent(matterId)}/disconnect/preview`
    const response = await fetch(apiUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
      },
    })

    const { data, parseError } = await readResponseJson(response)
    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatUserFilesErrorDetail(errorData)
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterId,
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
          },
        })
      })
      throw new ApiError(response.status, detail || `Failed to load matter disconnect preview: ${response.status}`)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Matter external sync mapping disconnect preview response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterId,
            parseError: formatUnknownError(parseError),
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid matter external sync mapping disconnect preview response JSON')
    }

    const parsed = MatterExternalSyncMappingDisconnectPreviewSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Matter external sync mapping disconnect preview response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            matterId,
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid matter external sync mapping disconnect preview response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, { extra: { matterId } })
      })
    }
    throw error
  }
}
